import { useState } from 'react';
import { paymentsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CreditCard, Banknote, Smartphone, Building2, Check, RotateCcw, LogOut, Loader2, PartyPopper } from 'lucide-react';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'upi', label: 'UPI', icon: Smartphone },
  { value: 'card', label: 'Card', icon: CreditCard },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: Building2 },
];

export default function StepPayment({ wizardState, updateState, resetWizard, toggleClientMode }) {
  const [method, setMethod] = useState('cash');
  const [amount, setAmount] = useState(Math.round((wizardState.inclusivePrice || 0) * 0.5));
  const [recording, setRecording] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [reference, setReference] = useState('');

  const total = wizardState.inclusivePrice || 0;
  const advance = Math.round(total * 0.5);

  const recordPayment = async () => {
    if (!wizardState.orderId) {
      toast.error('Order not found — please go back');
      return;
    }
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setRecording(true);
    try {
      await paymentsAPI.record({
        order_id: wizardState.orderId,
        amount: parseFloat(amount),
        method,
        reference: reference || undefined,
        notes: 'Advance payment via Client Mode',
      });
      setCompleted(true);
      toast.success('Payment recorded!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to record payment';
      toast.error(msg);
    }
    setRecording(false);
  };

  // Completion screen
  if (completed) {
    return (
      <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="step-payment-complete">
        <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(5,150,105,0.1)' }}>
          <Check className="w-10 h-10" style={{ color: '#059669' }} />
        </div>

        <h1 className="text-3xl font-light" style={{ color: 'var(--brand-navy)', fontFamily: "'Playfair Display', serif" }}>
          Order Placed!
        </h1>
        <p className="mt-3 text-base" style={{ color: 'var(--ink-muted)' }}>
          We'll keep {wizardState.client?.first_name} updated on WhatsApp as your garment progresses.
        </p>

        <div className="mt-8 p-5 rounded-2xl inline-block" style={{ backgroundColor: 'rgba(27,42,74,0.03)', border: '1px solid rgba(27,42,74,0.06)' }}>
          <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>Order Total</p>
          <p className="text-3xl font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
            &#8377;{total.toLocaleString('en-IN')}
          </p>
          <p className="text-xs mt-1" style={{ color: '#059669' }}>
            &#8377;{parseFloat(amount).toLocaleString('en-IN')} paid via {method.replace('_', ' ')}
          </p>
        </div>

        <div className="mt-10 flex flex-col items-center gap-3">
          <Button
            onClick={resetWizard}
            className="h-14 px-10 rounded-full text-sm font-semibold gap-2 shadow-lg"
            style={{ backgroundColor: 'var(--brand-gold)', color: 'var(--brand-navy)' }}
            data-testid="start-new-consultation-btn"
          >
            <RotateCcw className="w-4 h-4" /> Start New Consultation
          </Button>
          <Button
            onClick={toggleClientMode}
            variant="ghost"
            className="gap-2 text-xs"
            style={{ color: 'var(--ink-muted)' }}
            data-testid="exit-client-mode-final-btn"
          >
            <LogOut className="w-3 h-3" /> Exit Client Mode
          </Button>
        </div>
      </div>
    );
  }

  // Payment screen
  return (
    <div className="max-w-lg mx-auto px-6 py-10" data-testid="step-payment">
      <div className="text-center mb-10">
        <h1 className="text-3xl lg:text-4xl font-light tracking-tight" style={{ color: 'var(--brand-navy)', fontFamily: "'Playfair Display', serif" }}>
          Confirm & Pay
        </h1>
        <p className="mt-2 text-base" style={{ color: 'var(--ink-muted)' }}>
          Secure your order with an advance payment
        </p>
      </div>

      {/* Order total */}
      <div className="text-center mb-8 p-6 rounded-2xl" style={{ backgroundColor: 'white', border: '1px solid rgba(27,42,74,0.06)' }}>
        <p className="text-xs uppercase tracking-widest font-medium" style={{ color: 'var(--ink-muted)' }}>Order Total</p>
        <p className="text-4xl font-bold tabular-nums mt-1" style={{ color: 'var(--brand-navy)' }}>
          &#8377;{total.toLocaleString('en-IN')}
        </p>
        <p className="text-sm mt-2" style={{ color: 'var(--brand-gold)' }}>
          Advance Due (50%): &#8377;{advance.toLocaleString('en-IN')}
        </p>
      </div>

      {/* Payment method */}
      <div className="mb-6">
        <Label className="text-xs uppercase tracking-widest font-medium mb-3 block" style={{ color: 'var(--ink-muted)' }}>
          Payment Method
        </Label>
        <div className="grid grid-cols-2 gap-3">
          {PAYMENT_METHODS.map(m => {
            const Icon = m.icon;
            const isActive = method === m.value;
            return (
              <button
                key={m.value}
                onClick={() => setMethod(m.value)}
                className="flex items-center gap-3 p-4 rounded-xl border-2 transition-all"
                style={{
                  borderColor: isActive ? 'var(--brand-gold)' : 'rgba(27,42,74,0.06)',
                  backgroundColor: isActive ? 'rgba(197,165,90,0.05)' : 'white',
                }}
                data-testid={`payment-method-${m.value}`}
              >
                <Icon className="w-5 h-5" style={{ color: isActive ? 'var(--brand-gold)' : 'var(--ink-muted)' }} />
                <span className="text-sm font-medium" style={{ color: isActive ? 'var(--brand-navy)' : 'var(--ink-muted)' }}>{m.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Amount */}
      <div className="mb-6">
        <Label className="text-xs uppercase tracking-widest font-medium mb-2 block" style={{ color: 'var(--ink-muted)' }}>
          Amount
        </Label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold" style={{ color: 'var(--brand-navy)' }}>&#8377;</span>
          <Input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            className="h-14 pl-10 rounded-xl text-xl font-bold tabular-nums"
            style={{ color: 'var(--brand-navy)' }}
            data-testid="payment-amount-input"
          />
        </div>
        <div className="flex gap-2 mt-2">
          <button onClick={() => setAmount(advance)} className="text-xs px-3 py-1 rounded-full border transition-all hover:bg-gray-50"
            style={{ borderColor: 'rgba(27,42,74,0.1)', color: 'var(--ink-muted)' }}>
            50% (&#8377;{advance.toLocaleString('en-IN')})
          </button>
          <button onClick={() => setAmount(total)} className="text-xs px-3 py-1 rounded-full border transition-all hover:bg-gray-50"
            style={{ borderColor: 'rgba(27,42,74,0.1)', color: 'var(--ink-muted)' }}>
            Full (&#8377;{total.toLocaleString('en-IN')})
          </button>
        </div>
      </div>

      {/* Reference */}
      {(method === 'upi' || method === 'card' || method === 'bank_transfer') && (
        <div className="mb-8">
          <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>Reference / Transaction ID (optional)</Label>
          <Input
            value={reference}
            onChange={e => setReference(e.target.value)}
            className="h-12 rounded-xl mt-1"
            placeholder="e.g. UPI ref number"
            data-testid="payment-reference-input"
          />
        </div>
      )}

      {/* Record payment */}
      <Button
        onClick={recordPayment}
        disabled={recording}
        className="w-full h-14 rounded-full text-base font-semibold gap-2 shadow-lg"
        style={{ backgroundColor: 'var(--brand-navy)' }}
        data-testid="record-payment-btn"
      >
        {recording ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
        {recording ? 'Recording...' : `Record Payment`}
      </Button>
    </div>
  );
}
